import { useMemo, useCallback } from 'react';
import { useGetCartQuery } from '../../orders/services/orderApi';
import { AssetCartItem } from '../../orders/cart/types/cart.types';
import { getPermissionForAction } from '../../../util/permissions';

/**
 * All photo assetIds a grouped line covers (DV-2131). Mirrors toGroupedRow:
 * prefer assetRefs; fall back to the singular assetId for confirmation-style
 * rows that carry no refs.
 */
const lineAssetIds = (item: AssetCartItem): string[] =>
  item.assetRefs?.length
    ? item.assetRefs.map((r) => r.assetId)
    : item.assetId
      ? [item.assetId]
      : [];

export function useAssetCart(show: boolean) {
  const userId = localStorage.getItem('userId') ?? '';

  const { data: cartResponse } = useGetCartQuery(userId, {
    // Also skip for roles denied 'GET :Cart' (INTERNAL_POST_PROCESSING) so a 403
    // can't drive baseQueryWithAuth's refresh-token rotation / logout (DV-2751).
    skip: !show || !userId || !getPermissionForAction('Cart', 'GET'),
  });

  const cartEditPhotoOrders: AssetCartItem[] = useMemo(
    () => cartResponse?.editPhotoOrders ?? [],
    [cartResponse]
  );

  const cartAssetIdSet = useMemo(() => {
    const ids = new Set<string>();
    cartEditPhotoOrders.forEach((item) =>
      lineAssetIds(item).forEach((id) => ids.add(id))
    );
    return ids;
  }, [cartEditPhotoOrders]);

  const getCartItemForService = useCallback(
    (assetId: string, serviceSlug: string) =>
      cartEditPhotoOrders.find(
        (item) =>
          item.service?.shoppingCartSlug === serviceSlug &&
          lineAssetIds(item).includes(assetId)
      ) ?? null,
    [cartEditPhotoOrders]
  );

  const getCartServiceSlugsForAsset = useCallback(
    (assetId: string) => {
      const slugs = new Set<string>();
      cartEditPhotoOrders
        .filter((item) => lineAssetIds(item).includes(assetId))
        .forEach((item) => {
          if (item.service?.shoppingCartSlug)
            slugs.add(item.service.shoppingCartSlug);
        });
      return slugs;
    },
    [cartEditPhotoOrders]
  );

  // The cart line for `serviceSlug` whose photo set EXACTLY matches `assetIds`
  // (order-independent), or null. Lets the assets page recognise a re-pick of
  // the same images + same service and reopen that line in edit mode instead
  // of firing a blind add. Exact-match only: a subset/superset selection is a
  // different intent and falls through to a fresh add.
  const getExactCartLineForSelection = useCallback(
    (serviceSlug: string, assetIds: string[]): AssetCartItem | null => {
      if (assetIds.length === 0) return null;
      const selected = new Set(assetIds);
      return (
        cartEditPhotoOrders.find((item) => {
          if (item.service?.shoppingCartSlug !== serviceSlug) return false;
          const lineIds = lineAssetIds(item);
          return (
            lineIds.length === selected.size &&
            lineIds.every((id) => selected.has(id))
          );
        }) ?? null
      );
    },
    [cartEditPhotoOrders]
  );

  return {
    cartAssetIdSet,
    getCartItemForService,
    getCartServiceSlugsForAsset,
    getExactCartLineForSelection,
  };
}
